import { useState, useEffect, useRef, useCallback } from 'react';
import Services from '../services/Services';
import { registerAbortController, unregisterAbortController, isLoggingOut } from '../utils/requestCancellation';
import { SegmentWithCount } from '../types/segmentation.types';

interface UseSegmentationsOptions {
  token: string | null;
  channelID: string | null | undefined;
}

interface UseSegmentationsReturn {
  segments: string[];
  segmentsWithCount: SegmentWithCount[];
  loading: boolean;
  error: string | null;
  addSegment: (tag: string) => Promise<boolean>;
  removeSegment: (tag: string) => Promise<boolean>;
  addTagToUsers: (tag: string, userIds: string[]) => Promise<boolean>;
  removeTagFromUsers: (tag: string, userIds: string[]) => Promise<boolean>;
  refreshSegments: () => void;
}

export const useSegmentations = (options: UseSegmentationsOptions): UseSegmentationsReturn => {
  const { token, channelID } = options;
  const [segmentsWithCount, setSegmentsWithCount] = useState<SegmentWithCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch segments with user count
  useEffect(() => {
    const abortController = new AbortController();
    registerAbortController(abortController);

    const fetchSegments = async () => {
      if (!token || !channelID || isLoggingOut()) {
        if (isMountedRef.current) {
          setSegmentsWithCount([]);
          setLoading(false);
        }
        return;
      }

      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }

      try {
        const response = await Services.SocialHiveGetChannelSegmentationsWithUserCount(
          token,
          channelID,
          abortController
        );

        if (isMountedRef.current) {
          if (response.data.status && response.data.result) {
            const result = response.data.result as any;
            const tagsData = result.tags || result;
            if (Array.isArray(tagsData)) {
              const parsed: SegmentWithCount[] = tagsData.map((item: any) => {
                if (typeof item === 'string') {
                  return { tag: item, userCount: 0 };
                }
                return {
                  tag: item.tag,
                  userCount: parseInt(String(item.userCount || '0'), 10),
                };
              }).filter((item: SegmentWithCount) => item.tag);
              setSegmentsWithCount(parsed);
            } else {
              setSegmentsWithCount([]);
            }
          } else {
            setSegmentsWithCount([]);
          }
          setLoading(false);
        }
      } catch (err: any) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          return;
        }
        if (isMountedRef.current) {
          setError(err.message || 'Failed to fetch segments');
          setSegmentsWithCount([]);
          setLoading(false);
        }
      }
    };

    fetchSegments();

    return () => {
      abortController.abort();
      unregisterAbortController(abortController);
    };
  }, [token, channelID, refreshTrigger]);

  // Derive string[] for backwards compatibility
  const segments = segmentsWithCount.map(s => s.tag);

  const refreshSegments = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const addSegment = useCallback(async (tag: string): Promise<boolean> => {
    if (!token || !channelID) return false;

    try {
      const response = await Services.SocialHiveAddSegmentationToChannel(
        token,
        channelID,
        tag
      );

      if (response.data.status) {
        // Optimistic update
        if (isMountedRef.current) {
          setSegmentsWithCount(prev => [...prev, { tag, userCount: 0 }]);
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to add segment:', err);
      return false;
    }
  }, [token, channelID]);

  const removeSegment = useCallback(async (tag: string): Promise<boolean> => {
    if (!token || !channelID) return false;

    try {
      const response = await Services.SocialHiveRemoveSegmentationFromChannel(
        token,
        channelID,
        tag
      );

      if (response.data.status) {
        // Optimistic update
        if (isMountedRef.current) {
          setSegmentsWithCount(prev => prev.filter(s => s.tag !== tag));
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to remove segment:', err);
      return false;
    }
  }, [token, channelID]);

  const addTagToUsers = useCallback(async (tag: string, userIds: string[]): Promise<boolean> => {
    if (!token || !channelID || userIds.length === 0) return false;

    try {
      const response = await Services.SocialHiveAddChannelTagToUsers(
        token,
        channelID,
        tag,
        userIds
      );

      return response.data.status;
    } catch (err) {
      console.error('Failed to add tag to users:', err);
      return false;
    }
  }, [token, channelID]);

  const removeTagFromUsers = useCallback(async (tag: string, userIds: string[]): Promise<boolean> => {
    if (!token || !channelID || userIds.length === 0) return false;

    try {
      const response = await Services.SocialHiveRemoveChannelTagFromUsers(
        token,
        channelID,
        tag,
        userIds
      );

      return response.data.status;
    } catch (err) {
      console.error('Failed to remove tag from users:', err);
      return false;
    }
  }, [token, channelID]);

  return {
    segments,
    segmentsWithCount,
    loading,
    error,
    addSegment,
    removeSegment,
    addTagToUsers,
    removeTagFromUsers,
    refreshSegments,
  };
};

export default useSegmentations;
